import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Upload, CheckCircle2, FileText, Image as ImageIcon, X } from "lucide-react";

/**
 * ComprobanteUploadBlock
 * ──────────────────────
 * Bloque para que el usuario suba el comprobante de pago (Bre-B, transferencia, etc.).
 *
 * Props:
 *  - caseId: string
 *  - nivel: "nivel1" | "nivel2"
 *  - paymentStatus: estado actual del pago (para ocultar el upload si ya está confirmado)
 *  - alreadyUploaded: boolean (si ya hay comprobante)
 *  - uploadedAt: ISO string (opcional, fecha de la última subida)
 *  - filename: nombre del archivo subido
 *  - onUploaded: callback tras subida exitosa (refresca el padre)
 */
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

export default function ComprobanteUploadBlock({
  caseId,
  nivel,
  paymentStatus,
  alreadyUploaded,
  uploadedAt,
  filename,
  onUploaded,
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    if (!file.type.startsWith("image/")) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_TYPES.includes(f.type)) {
      toast.error("Formato no soportado. Permitidos: JPG, PNG, WEBP o PDF.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_SIZE) {
      toast.error(`Archivo demasiado grande. Máximo 5 MB (subiste ${(f.size / 1024 / 1024).toFixed(1)} MB).`);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const upload = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/cases/${caseId}/payments/${nivel}/comprobante`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Comprobante subido. El equipo lo revisará para confirmar tu pago.");
      setFile(null);
      onUploaded?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error subiendo el comprobante");
    } finally {
      setUploading(false);
    }
  }, [file, caseId, nivel, onUploaded]);

  if (paymentStatus === "confirmed" || paymentStatus === "refunded") return null;

  return (
    <div className="border-2 border-dashed border-primary/40 bg-primary/[0.02] p-5" data-testid={`comprobante-block-${nivel}`}>
      <div className="flex items-start gap-3 mb-3">
        <Upload className="w-5 h-5 text-primary mt-0.5 shrink-0" />
        <div className="flex-1">
          <h4 className="text-sm font-medium text-gray-900 mb-1">Subir comprobante de pago</h4>
          <p className="text-xs text-gray-600">
            ¿Ya pagaste por Bre-B o transferencia? Sube una captura o PDF del comprobante para que validemos tu pago más rápido (máx. 5 MB).
          </p>
        </div>
      </div>

      {alreadyUploaded && (
        <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded-sm p-3 flex items-start gap-2" data-testid="comprobante-already-uploaded">
          <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-xs text-emerald-900 flex-1">
            <p>
              <strong>Comprobante recibido</strong>
              {filename && <> · {filename}</>}
            </p>
            {uploadedAt && (
              <p className="text-emerald-800 mt-0.5">
                Subido el {new Date(uploadedAt).toLocaleString("es-CO")}. En revisión por el equipo.
              </p>
            )}
            <p className="text-emerald-800 mt-0.5">
              Puedes subir un nuevo archivo si necesitas reemplazarlo.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <label
          htmlFor={`comprobante-file-${nivel}`}
          className="block border border-gray-300 bg-white rounded-sm px-3 py-2 cursor-pointer hover:border-primary transition-colors text-sm"
          data-testid="comprobante-file-label"
        >
          {file ? (
            <span className="flex items-center gap-2 text-gray-800">
              {file.type === "application/pdf" ? <FileText className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
              {file.name} ({(file.size / 1024).toFixed(0)} KB)
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setFile(null); }}
                className="ml-2 text-gray-500 hover:text-rose-600"
                aria-label="Quitar archivo"
                data-testid="comprobante-remove-file"
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          ) : (
            <span className="text-gray-500">Selecciona archivo (JPG, PNG, WEBP, PDF)…</span>
          )}
          <input
            id={`comprobante-file-${nivel}`}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={onFileChange}
            className="hidden"
            data-testid="comprobante-file-input"
          />
        </label>

        {preview && (
          <img src={preview} alt="Vista previa" className="max-w-[240px] border border-gray-200 rounded-sm" data-testid="comprobante-preview" />
        )}

        <Button
          onClick={upload}
          disabled={!file || uploading}
          className="rounded-sm h-11 px-6"
          data-testid="comprobante-submit"
        >
          {uploading ? "Subiendo…" : alreadyUploaded ? "Reemplazar comprobante" : "Subir comprobante"}
        </Button>
      </div>
    </div>
  );
}
